import captureError from '@disco/utils/captureError'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'hooks/use-redux'
import api from '@disco/api/server'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import DownloadLink from 'components/common/download-link'
import DocumentHeadTags from 'components/document-head-tags'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Stack from 'components/ui/stack'

const DataManagementNesting = (): React.ReactNode => {
  const dispatch = useDispatch()
  const [downloadUrl, setDownloadUrl] = useState<string | null | undefined>()
  const [isAwaiting, setIsAwaiting] = useState<boolean>(false)
  const [isDeferred, setIsDeferred] = useState<boolean>(false)
  const handleExport = useCallback(async () => {
    if (!isAwaiting) {
      try {
        setIsAwaiting(true)
        const response = await api.exportTrackMetadata({
          export_nested_tracks: true,
        })

        if (response.deferred) {
          setIsDeferred(true)
        }

        if (response.download_url) {
          setDownloadUrl(response.download_url)
        }
      } catch (error) {
        pushUINotification({
          level: 'error',
          message: parseServerError(error),
        })
        captureError(error)
      }

      setIsAwaiting(false)
    }
  }, [dispatch, isAwaiting])
  return (
    <>
      <DocumentHeadTags title="Nesting | DISCO" />
      <PageHeader title="Nesting" />
      <PageContainer>
        <PageContainer.Section title="Export nesting CSV">
          <Stack>
            <Alert
              actions={
                <Button
                  appearance="inline-link"
                  caption="Find out more"
                  link="https://support.disco.ac/home/track-nesting-guide#managing-nested-tracks-in-bulk"
                  linkTarget="_blank"
                />
              }
              icon="info"
              status="info"
            >
              Here you can export your catalog to CSV and then associate your
              nested versions in the CSV. Once you’ve done this, contact support
              to re-import your CSV and nest your tracks.
            </Alert>
            <Button
              appearance="primary"
              caption="Export"
              progressCaption="Generating export"
              progressIndicator={isAwaiting}
              onClick={handleExport}
            />
          </Stack>
        </PageContainer.Section>
        {Boolean(downloadUrl) && <DownloadLink url={downloadUrl} />}
        {isDeferred && (
          <ModalDialog
            title="Your export is being generated"
            primaryAction={{
              caption: 'Close',
              onClick: () => {
                setIsDeferred(false)
              },
            }}
          >
            We’re generating your export now, and will email it to you as soon
            as it’s ready. Please note, during peak periods, exports can take up
            to an hour to generate.
          </ModalDialog>
        )}
      </PageContainer>
    </>
  )
}

export default DataManagementNesting
